/* jrs-size-surcharge.js: larger-size surcharge line for ready-made bundles (2026-11-02).
 *
 * Jim 2026-11-02: 25% surcharge on 3XL and up in ready-made bundles. Per item:
 * 25% of the item's share of the bundle price (bundle line price / garments in the
 * bundle), rounded to the penny. Charged as ONE separate cart line per bundle on the
 * hidden product "larger-size-surcharge" (one variant per amount), qty = 3XL+ garments
 * x bundle line qty. The bundle line, its price and its properties stay untouched, so
 * Google Shopping still matches the PDP and order-alerts reads the bundle as before.
 *
 * Surcharge line properties: `_jrs_size_surcharge_ref` = the bundle's
 * `_bundle_instance_id` (deliberately NOT `_bundle_instance_id` or `_ParentVariant`,
 * so the bundle remove cascade, the cart validator and order-alerts all treat it as a
 * plain standalone line).
 *
 * Reconcile = read /cart.js, work out the wanted surcharge line per bundle, then add /
 * change / remove so the cart matches. Runs:
 *   - after a bundle add (wraps JRS.CartSubmit.submitAndVerify, before the redirect to /cart);
 *   - on the cart page at load and after every cart:updated (bundle qty change or removal).
 * Fails open: any error leaves the cart as it is and never blocks an add or checkout.
 * Kill switch: no config (product unpublished / missing) -> script does nothing.
 */
(function () {
  'use strict';

  var CFG = window.JRS_SIZE_SURCHARGE;
  if (!CFG || !CFG.variants) { return; }

  var REF_KEY = '_jrs_size_surcharge_ref';
  var PCT = Number(CFG.pct) || 25;
  var MAX_OPS = 20;                  // more changes than this in one pass = something is off: stop
  // 3XL and up. Also XXXL / XXXXL spellings and 10XL+.
  var BIG_SIZE = /^(3XL|XXXL|4XL|XXXXL|[5-9]XL|\d{2}XL)$/i;

  // amount (pence) -> variant id, plus a sorted list for the nearest-amount fallback
  var AMOUNTS = [];
  for (var k in CFG.variants) {
    if (Object.prototype.hasOwnProperty.call(CFG.variants, k)) { AMOUNTS.push(Number(k)); }
  }
  AMOUNTS.sort(function (a, b) { return a - b; });

  function isBig(size) { return BIG_SIZE.test(String(size == null ? '' : size).replace(/\s+/g, '')); }

  // Garments in one bundle from `_bundle_raw`: an object (form-encoded nested hash,
  // as /cart.js returns it) or a string (JSON or Ruby Hash#inspect).
  function garments(raw) {
    var out = [];
    function walk(node) {
      if (!node || typeof node !== 'object') { return; }
      if (node.size !== undefined && node.quantity !== undefined && typeof node.size !== 'object') {
        out.push({ size: String(node.size), qty: parseInt(node.quantity, 10) || 0 });
        return;
      }
      for (var key in node) {
        if (Object.prototype.hasOwnProperty.call(node, key)) { walk(node[key]); }
      }
    }
    if (raw && typeof raw === 'object') { walk(raw); return out; }
    if (typeof raw !== 'string' || !raw) { return out; }
    try { walk(JSON.parse(raw)); if (out.length) { return out; } } catch (e) { /* not JSON */ }
    var re = /"size"\s*(?:=>|:)\s*"([^"]*)"\s*,\s*"quantity"\s*(?:=>|:)\s*"?(\d+)/g;
    var m;
    while ((m = re.exec(raw)) !== null) { out.push({ size: m[1], qty: parseInt(m[2], 10) || 0 }); }
    return out;
  }

  // 3XL+ garments with name and colour, for the cart "See details" popup (Hamza 2026-11-04).
  // Object or JSON `_bundle_raw` only (what /cart.js returns); size nodes carry name/color.
  function bigDetails(raw) {
    var out = [];
    function walk(node) {
      if (!node || typeof node !== 'object') { return; }
      if (node.size !== undefined && node.quantity !== undefined && typeof node.size !== 'object') {
        var q = parseInt(node.quantity, 10) || 0;
        if (q > 0 && isBig(node.size)) { out.push({ name: String(node.name || ''), color: String(node.color || ''), size: String(node.size), qty: q }); }
        return;
      }
      for (var key in node) {
        if (Object.prototype.hasOwnProperty.call(node, key)) { walk(node[key]); }
      }
    }
    if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch (e) { return out; } }
    walk(raw);
    return out;
  }

  function variantFor(amount) {
    if (CFG.variants[String(amount)]) { return Number(CFG.variants[String(amount)]); }
    // Partial bundle (fewer garments than the bundle size) -> amount not in the list:
    // use the nearest listed amount rather than skip the charge.
    if (!AMOUNTS.length) { return null; }
    var best = AMOUNTS[0];
    for (var i = 1; i < AMOUNTS.length; i++) {
      if (Math.abs(AMOUNTS[i] - amount) < Math.abs(best - amount)) { best = AMOUNTS[i]; }
    }
    return Number(CFG.variants[String(best)]);
  }

  // Visible line properties on the surcharge line, shown at checkout and on the order
  // (Hamza 2026-11-04: checkout needs to say which garments and sizes it covers).
  var PROP_BUNDLE = 'Bundle';
  var PROP_SIZES = 'Sizes';
  function sizesText(raw) {
    var rows = bigDetails(raw), groups = [], byKey = {};
    for (var i = 0; i < rows.length; i++) {
      var k = rows[i].name + '|' + rows[i].color;
      if (!byKey[k]) { byKey[k] = { label: rows[i].name + (rows[i].color ? ', ' + rows[i].color : ''), parts: [] }; groups.push(byKey[k]); }
      byKey[k].parts.push(rows[i].size + ' × ' + rows[i].qty);
    }
    var out = [];
    for (var g = 0; g < groups.length; g++) { out.push((groups[g].label ? groups[g].label + ': ' : '') + groups[g].parts.join(', ')); }
    return out.join('; ');
  }

  // Wanted surcharge lines, keyed by bundle instance id.
  function wanted(cart) {
    var want = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var p = it.properties || {};
      if (p._ParentVariant != null || p._bundle_raw == null || !p._bundle_instance_id) { continue; }
      var list = garments(p._bundle_raw);
      var total = 0, big = 0;
      for (var g = 0; g < list.length; g++) {
        total += list[g].qty;
        if (isBig(list[g].size)) { big += list[g].qty; }
      }
      if (!big || !total) { continue; }
      // Same maths as the PDP note (sections/bundle-new.liquid): price * pct / garments, rounded.
      var amount = Math.round(Number(it.original_price) * (PCT / 100) / total);
      var variantId = amount > 0 ? variantFor(amount) : null;
      if (!variantId) { continue; }
      var ref = String(p._bundle_instance_id);
      var qty = big * (parseInt(it.quantity, 10) || 1);
      if (want[ref]) { want[ref].qty += qty; } else { want[ref] = { variantId: variantId, qty: qty, bundle: String(it.product_title || ''), sizes: sizesText(p._bundle_raw) }; }
    }
    return want;
  }

  // Operations that take the cart to the wanted state.
  function plan(cart) {
    var want = wanted(cart);
    var done = {};
    var ops = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var ref = it.properties && it.properties[REF_KEY];
      if (!ref) { continue; }
      var w = want[String(ref)];
      if (!w || done[ref] || Number(it.variant_id) !== w.variantId || String(it.properties[PROP_SIZES] || '') !== w.sizes) {
        ops.push({ type: 'change', key: it.key, quantity: 0 });   // orphan, duplicate, price changed or sizes changed
        continue;
      }
      done[ref] = true;
      if (Number(it.quantity) !== w.qty) { ops.push({ type: 'change', key: it.key, quantity: w.qty }); }
    }
    var adds = [];
    for (var r in want) {
      if (Object.prototype.hasOwnProperty.call(want, r) && !done[r]) {
        var props = {};
        props[REF_KEY] = r;
        if (want[r].bundle) { props[PROP_BUNDLE] = want[r].bundle; }
        if (want[r].sizes) { props[PROP_SIZES] = want[r].sizes; }
        adds.push({ id: want[r].variantId, quantity: want[r].qty, properties: props });
      }
    }
    if (adds.length) { ops.push({ type: 'add', items: adds }); }
    return ops;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) { throw new Error(url + ' ' + r.status); } return r.json(); });
  }

  // Write-path read (empty /cart/update.js): right after an add, a cached /cart.js
  // can miss the new bundle, and then no surcharge gets added (Hamza 2026-11-04).
  function getCart() {
    return fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: '{"updates":{}}' })
      .then(function (r) { if (!r.ok) { throw new Error('/cart/update.js ' + r.status); } return r.json(); });
  }

  // Direct fetch (not JRS.CartAPI) on purpose: CartAPI.addItems emits cart:addSucceeded,
  // which analytics reads as a shopper add_to_cart.
  function run(ops) {
    return ops.reduce(function (chain, op) {
      return chain.then(function () {
        return op.type === 'add'
          ? post('/cart/add.js', { items: op.items })
          : post('/cart/change.js', { id: op.key, quantity: op.quantity });
      });
    }, Promise.resolve());
  }

  var inflight = null;
  var again = false;
  // Resolves to the number of changes made (0 = cart already right). Never rejects.
  function reconcile() {
    if (inflight) { again = true; return inflight; }
    inflight = getCart().then(function (cart) {
      var ops = plan(cart);
      if (!ops.length) { return 0; }
      if (ops.length > MAX_OPS) { throw new Error('too many changes: ' + ops.length); }
      return run(ops).then(function () { return ops.length; });
    }).catch(function (err) {
      try { console.warn('[size-surcharge] skipped:', err && err.message); } catch (e) { /* noop */ }
      return 0;
    }).then(function (n) {
      inflight = null;
      if (again) { again = false; return reconcile().then(function (m) { return n + m; }); }
      return n;
    });
    return inflight;
  }

  // 1) After a bundle add, before the review step redirects to /cart.
  var JRS = window.JRS = window.JRS || {};
  if (JRS.CartSubmit && typeof JRS.CartSubmit.submitAndVerify === 'function' && !JRS.CartSubmit.__sizeSurcharge) {
    var orig = JRS.CartSubmit.submitAndVerify;
    JRS.CartSubmit.submitAndVerify = function (productConfiguration) {
      var res = orig.apply(this, arguments);
      if (!productConfiguration || productConfiguration.type !== 'bundle' || !res || typeof res.then !== 'function') { return res; }
      return res.then(function (result) {
        return reconcile().then(function () { return result; }, function () { return result; });
      });
    };
    JRS.CartSubmit.__sizeSurcharge = true;
  }

  // ---------------------------------------------------------------------------
  // Display (Hamza 2026-11-04, style C): one "Larger sizes" row that taps open to
  // list the garments, colours and sizes charged plus a one-line reason. Used on the
  // PDP totals box, the customiser review card and the cart (cart markup is Liquid in
  // snippets/cart-item.liquid, same classes). Under each size grid: a quiet receipt
  // row with "Why?" opening the reason in place (style A1). No popups anywhere.
  // ---------------------------------------------------------------------------
  function money(p) { return '£' + (p / 100).toFixed(2); }
  function esc(t) { return String(t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function reason(each) {
    return '3XL and up cost us more to buy in. We add ' + money(each) + ' to each so every other size stays at the bundle price.';
  }

  var CSS =
    '.jrs-ss-disc{border:1px solid #E5E5E5;border-radius:8px;background:#fff;margin-top:10px;text-align:left}' +
    '.jrs-ss-disc__head{display:flex;width:100%;justify-content:space-between;align-items:center;gap:12px;min-height:44px;margin:0;padding:0 12px;background:none;border:0;border-radius:8px;font:inherit;font-size:13px;line-height:1.3;color:#1A1A1A;cursor:pointer;text-align:left;letter-spacing:normal;text-transform:none}' +
    '.jrs-ss-disc__amt{display:inline-flex;align-items:center;font-weight:700;white-space:nowrap;font-variant-numeric:tabular-nums}' +
    '.jrs-ss-chev{display:inline-block;width:7px;height:7px;margin-left:8px;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;transform:translateY(-2px) rotate(45deg);transition:transform .15s cubic-bezier(.22,.61,.36,1)}' +
    '.jrs-ss-disc__head[aria-expanded="true"] .jrs-ss-chev{transform:translateY(2px) rotate(-135deg)}' +
    '.jrs-ss-disc__body{padding:0 12px 12px;font-size:12px;line-height:1.5;color:#636363}' +
    '.jrs-ss-disc__list{list-style:none;margin:0;padding:0}' +
    '.jrs-ss-disc__list li{display:flex;justify-content:space-between;gap:10px;margin:0;padding:8px 0;border-top:1px solid #E5E5E5;font-size:13px;line-height:1.4;color:#1A1A1A}' +
    '.jrs-ss-disc__list em{display:block;font-style:normal;font-size:12px;color:#636363}' +
    '.jrs-ss-disc__list li>span:last-child{white-space:nowrap;font-variant-numeric:tabular-nums}' +
    '.jrs-ss-disc__reason{margin:8px 0 0}' +
    '.jrs-ss-row{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin:10px 4px 0;padding-top:10px;border-top:1px solid #E5E5E5;font-size:12px;line-height:1.4;color:#636363}' +
    '.jrs-ss-row b{font-weight:600;color:#1A1A1A;white-space:nowrap;font-variant-numeric:tabular-nums}' +
    '.jrs-ss-why{display:inline;margin:0 0 0 4px;padding:0;min-height:0;min-width:0;background:none;border:0;font:inherit;letter-spacing:normal;text-transform:none;color:#C4510F;text-decoration:underline;text-underline-offset:2px;cursor:pointer}' +
    '.jrs-ss-row__reason{margin:4px 4px 0;font-size:12px;line-height:1.5;color:#636363}' +
    '.jrs-ss-disc__head:focus-visible,.jrs-ss-why:focus-visible{outline:2px solid #2563EB;outline-offset:2px}' +
    '.bundle-selector .stats-bar[style*="none"] ~ .jrs-ss-stats{display:none}' +
    // cart: the row sits in the price column's flex row; give it its own full-width line
    '.mrk-cart-item > .jrs-ss-disc{width:100%;box-sizing:border-box;margin-top:12px;margin-bottom:16px}' +
    // one-line header; on desktop the cart row is 340px wide and overflows left into the
    // empty space beside the narrow price column (Hamza 2026-11-04)
    '.jrs-ss-disc__head > span:first-child{white-space:nowrap}' +
    '@media (min-width:750px){.mrk-cart-item > .jrs-ss-disc{width:340px;margin-left:auto}}' +
    // review card: keep the image, title and Change button at the top when the row opens (Hamza 2026-11-04)
    '.jrs-product-card:has(.jrs-ss-review:not([hidden])){align-items:flex-start}' +
    '@media (prefers-reduced-motion:reduce){.jrs-ss-chev{transition:none}}';
  if (typeof document.createElement === 'function' && document.head && !document.getElementById('jrs-ss-style')) {
    var st = document.createElement('style');
    st.id = 'jrs-ss-style';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  // Open/closed state per row key, kept across re-renders.
  var openKeys = {};
  var seq = 0;
  // groups: [{ name, color, parts: ['3XL × 2', ...], qty }]
  function discHtml(key, count, each, groups, basis) {
    var open = !!openKeys[key];
    var id = 'jrs-ss-body-' + (++seq);
    var list = '';
    for (var i = 0; i < groups.length; i++) {
      list += '<li><span>' + esc(groups[i].name) + '<em>' + (groups[i].color ? esc(groups[i].color) + ' · ' : '') +
        esc(groups[i].parts.join(', ')) + '</em></span><span>' + money(groups[i].qty * each) + '</span></li>';
    }
    return '<div class="jrs-ss-disc" data-jrs-ss-key="' + esc(key) + '">' +
      '<button type="button" class="jrs-ss-disc__head" aria-expanded="' + open + '" aria-controls="' + id + '">' +
        '<span>Larger sizes · ' + count + ' × 3XL+</span>' +
        '<span class="jrs-ss-disc__amt">+' + money(count * each) + '<i class="jrs-ss-chev" aria-hidden="true"></i></span>' +
      '</button>' +
      '<div class="jrs-ss-disc__body" id="' + id + '"' + (open ? '' : ' hidden') + '>' +
        '<ul class="jrs-ss-disc__list">' + list + '</ul>' +
        '<p class="jrs-ss-disc__reason">' + esc(reason(each)) + (basis ? ' ' + esc(basis) : '') + '</p>' +
      '</div></div>';
  }
  // Re-render only when the content changed, so focus and the open state survive.
  function setHtml(el, html) {
    if (el.getAttribute('data-jrs-ss-sig') === html) { return; }
    el.setAttribute('data-jrs-ss-sig', html);
    el.innerHTML = html;
  }

  function toggleDisc(btn) {
    var box = btn.closest('.jrs-ss-disc');
    var body = box && box.querySelector('.jrs-ss-disc__body');
    if (!body) { return; }
    var open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    body.hidden = !open;
    openKeys[box.getAttribute('data-jrs-ss-key')] = open;
  }
  var rowReasonOpen = false;
  function toggleRowReason(btn) {
    rowReasonOpen = !rowReasonOpen;
    var rows = document.querySelectorAll('.jrs-ss-row-wrap');
    for (var i = 0; i < rows.length; i++) {
      var b = rows[i].querySelector('.jrs-ss-why');
      var r = rows[i].querySelector('.jrs-ss-row__reason');
      if (b) { b.textContent = rowReasonOpen ? 'Hide' : 'Why?'; b.setAttribute('aria-expanded', rowReasonOpen ? 'true' : 'false'); }
      if (r) { r.hidden = !rowReasonOpen; }
    }
    if (btn && btn.focus) { btn.focus(); }
  }
  // Capture phase: the bundle builder and customiser treat any click as a stepper
  // change, so these toggles stop the event here.
  if (typeof document.addEventListener === 'function') {
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) { return; }
      var head = t.closest('.jrs-ss-disc__head');
      if (head) { e.preventDefault(); e.stopPropagation(); toggleDisc(head); return; }
      var why = t.closest('.jrs-ss-row-wrap .jrs-ss-why');
      if (why) { e.preventDefault(); e.stopPropagation(); toggleRowReason(why); }
    }, true);
  }

  // 2) Cart page: line rows are server-rendered, so reload once when this pass changed the cart.
  function onCartPage() { return /^\/cart\/?$/.test(window.location.pathname); }
  function reloadOnce() {
    var key = 'jrsSizeSurchargeReload';
    var last = 0;
    try { last = Number(window.sessionStorage.getItem(key)) || 0; } catch (e) { /* storage blocked */ }
    if (Date.now() - last < 10000) { return; }   // reloaded in the last 10s: don't loop
    try { window.sessionStorage.setItem(key, String(Date.now())); } catch (e) { /* storage blocked */ }
    window.location.reload();
  }
  function cartPagePass() { reconcile().then(function (n) { if (n > 0) { reloadOnce(); } }); }

  if (onCartPage()) {
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', cartPagePass); } else { cartPagePass(); }
    if (JRS.EventBus && typeof JRS.EventBus.on === 'function') {
      var timer = null;
      JRS.EventBus.on('cart:updated', function (data) {
        if (data && data.reason === 'cart_edit') { return; }   // cart page reloads itself; the load pass covers it
        clearTimeout(timer);
        timer = setTimeout(cartPagePass, 300);
      });
    }
  }

  // 3) Bundle PDP: nothing until a 3XL+ size is picked (Hamza 2026-11-03). Then
  //    "+£3.44 each" on that size box, a receipt row under that size grid, and the
  //    Larger sizes row under the totals box and on the review card. Display only;
  //    the charge itself is step 1.
  var amountEl = typeof document.querySelector === 'function' ? document.querySelector('[data-jrs-ss-amount]') : null;
  var AMOUNT = amountEl ? parseInt(amountEl.getAttribute('data-jrs-ss-amount'), 10) : 0;
  var pdpState = { count: 0, groups: [] };

  function pdpGroups(scope) {
    var inputs = scope.querySelectorAll('input.mrk-qty-item[size]');
    var groups = [], byKey = {}, count = 0;
    for (var i = 0; i < inputs.length; i++) {
      var size = inputs[i].getAttribute('size');
      var q = parseInt(inputs[i].value, 10) || 0;
      if (!q || !isBig(size)) { continue; }
      var name = String(inputs[i].getAttribute('productname') || '').replace(/\s+/g, ' ').trim();
      var color = String(inputs[i].getAttribute('color') || '').trim();
      var k = name + '|' + color;
      if (!byKey[k]) { byKey[k] = { name: name, color: color, parts: [], qty: 0 }; groups.push(byKey[k]); }
      byKey[k].parts.push(size + ' × ' + q);
      byKey[k].qty += q;
      count += q;
    }
    return { count: count, groups: groups };
  }

  function sizeRowHtml(n) {
    return '<div class="jrs-ss-row"><span>Larger sizes · ' + n + ' × 3XL+' +
      '<button type="button" class="jrs-ss-why" aria-expanded="' + rowReasonOpen + '">' + (rowReasonOpen ? 'Hide' : 'Why?') + '</button></span>' +
      '<b>+' + money(n * AMOUNT) + '</b></div>' +
      '<p class="jrs-ss-row__reason"' + (rowReasonOpen ? '' : ' hidden') + '>' + esc(reason(AMOUNT)) + '</p>';
  }

  // Hamza 2026-11-04: while "+£3.44 each" shows on a size box, hide its "N in stock"
  // line unless stock is low (LOW_STOCK or fewer), so the box doesn't carry three lines.
  var LOW_STOCK = 20;
  function stockLine(cell) {
    var els = cell.querySelectorAll('span, div, small, p');
    for (var i = 0; i < els.length; i++) {
      if (els[i].children.length === 0 && /^\s*\d+\s+in stock\s*$/i.test(els[i].textContent)) { return els[i]; }
    }
    return null;
  }
  function setStockHidden(cell, hide) {
    var el = stockLine(cell);
    if (!el) { return; }
    var n = parseInt(el.textContent, 10) || 0;
    if (hide && n > LOW_STOCK) { el.setAttribute('data-jrs-ss-hid', '1'); el.style.display = 'none'; }
    else if (el.getAttribute('data-jrs-ss-hid')) { el.removeAttribute('data-jrs-ss-hid'); el.style.display = ''; }
  }

  function refreshPdp() {
    // "+£3.44 each" on each picked 3XL+ size box
    var inputs = document.querySelectorAll('.bundle-build-options input.mrk-qty-item[size]');
    for (var i = 0; i < inputs.length; i++) {
      var q = parseInt(inputs[i].value, 10) || 0;
      var cell = inputs[i].closest ? inputs[i].closest('.sc') : null;
      if (!cell || !isBig(inputs[i].getAttribute('size'))) { continue; }
      var tag = cell.querySelector('.jrs-ss-cell-tag');
      if (q > 0 && !tag) {
        tag = document.createElement('span');
        tag.className = 'jrs-ss-cell-tag';
        tag.style.cssText = 'display:block;font-size:11px;line-height:1.3;color:#636363;';
        tag.textContent = '+' + money(AMOUNT) + ' each';
        var label = cell.querySelector('.sc__label');
        if (label && label.parentNode) { label.parentNode.insertBefore(tag, label.nextSibling); } else { cell.appendChild(tag); }
        setStockHidden(cell, true);
      } else if (q === 0 && tag) {
        tag.parentNode.removeChild(tag);
        setStockHidden(cell, false);
      }
    }
    // Receipt row under each size grid that has a 3XL+ picked (style A1)
    var blocks = document.querySelectorAll('.bundle-build-options .colour-block__sizes');
    for (var b = 0; b < blocks.length; b++) {
      var bn = pdpGroups(blocks[b]).count;
      var wrap = blocks[b].querySelector('.jrs-ss-row-wrap');
      if (bn > 0) {
        if (!wrap) { wrap = document.createElement('div'); wrap.className = 'jrs-ss-row-wrap'; blocks[b].appendChild(wrap); }
        setHtml(wrap, sizeRowHtml(bn));
      } else if (wrap) {
        wrap.parentNode.removeChild(wrap);
      }
    }
    // Larger sizes row: totals box and review card
    var scope = document.querySelector('.bundle-build-options') || document;
    pdpState = pdpGroups(scope);
    renderDiscs();
  }
  function renderDiscs() {
    var targets = [['totals', document.querySelectorAll('[data-jrs-ss-total]')], ['review', document.querySelectorAll('.jrs-ss-review')]];
    for (var t = 0; t < targets.length; t++) {
      for (var j = 0; j < targets[t][1].length; j++) {
        var el = targets[t][1][j];
        if (pdpState.count > 0) { setHtml(el, discHtml(targets[t][0], pdpState.count, AMOUNT, pdpState.groups, 'Prices ex VAT.')); el.hidden = false; } else { el.hidden = true; }
      }
    }
  }

  if (AMOUNT > 0) {
    var pdpTimer = null;
    var schedule = function () { clearTimeout(pdpTimer); pdpTimer = setTimeout(refreshPdp, 120); };
    // Steppers set input values from click handlers (no input event), so listen to clicks too.
    document.addEventListener('click', schedule, true);
    document.addEventListener('input', schedule, true);
    document.addEventListener('change', schedule, true);
    if (typeof MutationObserver === 'function') {
      // Review step: add the row under the product card price when the customiser renders it.
      new MutationObserver(function () {
        var rows = document.querySelectorAll('.jrs-product-card__price-row');
        var added = false;
        for (var m = 0; m < rows.length; m++) {
          var next = rows[m].nextElementSibling;
          if (next && next.classList && next.classList.contains('jrs-ss-review')) { continue; }
          var p = document.createElement('div');
          p.className = 'jrs-ss-review';
          p.hidden = true;
          rows[m].parentNode.insertBefore(p, rows[m].nextSibling);
          added = true;
        }
        if (added) { renderDiscs(); }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  JRS.SizeSurcharge = { reconcile: reconcile, _plan: plan, _garments: garments, _isBig: isBig, _discHtml: discHtml };
})();
